import {
    CONTACT_SESSION_TOKEN_PATTERN,
    CONTACT_VERIFICATION_CODE_PATTERN,
    LEGACY_CONTACT_TOKEN_PATTERN,
} from "./contact-token.contract.ts";
import { z } from "zod";

const CONTACT_SESSION_STATUSES = [
    "created",
    "handoff_opened",
    "kits_connected",
    "provider_email_sent",
    "awaiting_provider_response",
    "escalated_to_admin",
    "failed",
    "cancelled",
] as const;

const contactSessionStatusSchema = z.enum(CONTACT_SESSION_STATUSES);
export type ContactSessionStatus = z.infer<typeof contactSessionStatusSchema>;

export const contactMessageIntentSchema = z.enum([
    "available",
    "meet",
    "pricing",
    "health",
    "logistics",
    "similar",
]);
export type ContactMessageIntent = z.infer<typeof contactMessageIntentSchema>;
export const DEFAULT_CONTACT_MESSAGE_INTENT: ContactMessageIntent = "available";

export const contactQuestionSchema = z.string().trim().min(1).max(1000);

export const contactListingStateSchema = z.enum([
    "priced",
    "unpriced",
    "pending",
    "adopted",
]);
export type ContactListingState = z.infer<typeof contactListingStateSchema>;

export const contactSessionTriggerSchema = z.enum([
    "sheet_open",
    "open_messages",
    "copy_message",
]);
export type ContactSessionTrigger = z.infer<typeof contactSessionTriggerSchema>;

export {
    CONTACT_SESSION_TOKEN_PATTERN,
    CONTACT_VERIFICATION_CODE_PATTERN,
    LEGACY_CONTACT_TOKEN_PATTERN,
};

export const createContactSessionRequestSchema = z.object({
    listingId: z.string().min(1),
    intent: contactMessageIntentSchema.default(DEFAULT_CONTACT_MESSAGE_INTENT),
    question: contactQuestionSchema.optional(),
    trigger: contactSessionTriggerSchema.default("open_messages"),
});

export const resolveContactHandoffRequestSchema = z.object({
    token: z.string().trim(),
    intent: contactMessageIntentSchema.default(DEFAULT_CONTACT_MESSAGE_INTENT),
    question: contactQuestionSchema.optional(),
});

export const resolveContactLinkPreviewRequestSchema = z.object({
    token: z
        .string()
        .trim()
        .toUpperCase()
        .regex(CONTACT_VERIFICATION_CODE_PATTERN),
});

export const markContactHandoffOpenedRequestSchema = z.preprocess(
    (value) => value ?? {},
    z.object({
        intent: contactMessageIntentSchema.default(
            DEFAULT_CONTACT_MESSAGE_INTENT,
        ),
    }),
);

const contactSessionHandoffSchema = z.object({
    kitsEnvironmentTag: z.string().nullable(),
    kitsPhoneNumber: z.string(),
    message: z.string(),
    qrCodeUrl: z.url(),
    sendblueEnvironmentTag: z.string().nullable(),
    smsUrl: z.string(),
});

const contactSessionSummarySchema = z.object({
    id: z.string(),
    listingId: z.string(),
    organizationId: z.string().nullable(),
    status: contactSessionStatusSchema,
    token: z.string().regex(CONTACT_SESSION_TOKEN_PATTERN),
});

const contactVerificationSummarySchema = z.object({
    code: z.string().regex(CONTACT_SESSION_TOKEN_PATTERN),
    expiresAt: z.iso.datetime({ offset: true }),
    id: z.string().min(1),
});

export const createContactSessionResponseSchema = z
    .object({
        handoff: contactSessionHandoffSchema,
        intent: contactMessageIntentSchema,
        listingName: z.string(),
        session: contactSessionSummarySchema,
        verification: contactVerificationSummarySchema,
    })
    .refine(
        (response) => response.session.token === response.verification.code,
        {
            message: "The contact session token must be the verification code.",
            path: ["verification", "code"],
        },
    );

export const contactVerificationStatusResponseSchema = z.object({
    status: z.enum(["pending", "verified", "expired"]),
});

export type CreateContactSessionResponse = z.infer<
    typeof createContactSessionResponseSchema
>;
export type ContactVerificationStatusResponse = z.infer<
    typeof contactVerificationStatusResponseSchema
>;
